import { Button, DrawerStepperProgress } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { onErrorMessage, useValidationErrors } from '@/helpers'

import {
  QUERIES_ADMIN,
  useCreateSupplier,
  useSupplierDetail,
  useUpdateSupplier,
} from '@/services'

import { SupplierProps } from '@/types'

import { SupplierForm, schemaSupplier } from '@/components'

import type { DrawerSupplierFormProps } from './drawer-form.type'

export const SupplierDrawerForm = ({
  isOpen,
  onHandleOpen,
  supplierUuid,
}: DrawerSupplierFormProps) => {
  const { t } = useTranslation('screenSuppliers')

  const isNewRegister = !supplierUuid

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<SupplierProps>>({})

  const { data: dataSupplier } = useSupplierDetail(supplierUuid || '')

  const {
    isValid: isValidSupplier,
    errors: supplierErrors,
    onHandleListenErrors: onHandleListenErrorsSupplier,
  } = useValidationErrors(schemaSupplier(t), data)

  const { mutateAsync: mutateAsyncUpdateSupplier } = useUpdateSupplier()
  const { mutateAsync: mutateAsyncCreateSupplier } = useCreateSupplier()

  const onHandleComplete = () => {
    setData({})
    onHandleOpen(false)
  }

  const onSubmitSupplierForm = async () => {
    if (!isValidSupplier) {
      onHandleListenErrorsSupplier(true)
      return
    }

    onHandleListenErrorsSupplier(false)

    let response: {
      success: boolean
      message: string
      data?: SupplierProps
    } = {
      success: false,
      message: '',
      data: {} as SupplierProps,
    }

    if (isNewRegister) {
      response = await mutateAsyncCreateSupplier(data as SupplierProps)
    } else {
      response = await mutateAsyncUpdateSupplier(data as SupplierProps)
    }

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER.module],
      })

      onHandleComplete()
    } else {
      if (isNewRegister) {
        onErrorMessage(
          t('supplierFormErrorCreateTitle'),
          t('supplierFormErrorCreateMessage'),
        )
      } else {
        onErrorMessage(
          t('supplierFormErrorUpdateTitle'),
          t('supplierFormErrorUpdateMessage'),
        )
      }
    }
  }

  useEffect(() => {
    if (isNewRegister) {
      setData({})
    } else {
      if (dataSupplier?.data) {
        setData({
          ...dataSupplier.data,
        })
      }
    }
  }, [isOpen, dataSupplier, supplierUuid])

  return (
    <DrawerStepperProgress
      translations={{
        optional: t('supplierFormOptional'),
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={
        isNewRegister
          ? t('supplierFormTitleCreate')
          : t('supplierFormTitleUpdate')
      }
      description={
        isNewRegister
          ? t('supplierFormSubTitleCreate')
          : t('supplierFormSubTitleUpdate')
      }
      initialStep={'root'}
      totalSteps={1}
      steps={{
        root: {
          title: t('supplierFormGeneralFormTitle'),
          isOptional: false,
          backNavigate: 'root',
          stepNumber: 0,
          component: (
            <SupplierForm
              errors={supplierErrors as Record<string, string>}
              values={data || {}}
              onChangeValues={setData}
            />
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={() => onHandleOpen(false)}>
                {t('supplierFormNavigateButtonBack')}
              </Button>

              <Button onClick={() => onSubmitSupplierForm()}>
                {t('supplierProductsFormNavigateButtonSave')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
